/**
 * trackerPick — a GPS tracker, as one row of a picker.
 *
 * THE SERIAL IS THE TRACKER'S NAME. `get_preparation_status` emits only `tracker_id`, the
 * `gps_trackers` primary key, and the area printed that at admins as `#95` — a number stamped on
 * nothing, written on no invoice, and impossible to check against the box in your hand. The serial
 * number is; the SIM in it is the second thing anybody asks about. Both come from the same
 * `fetchVehicleTrackers` join the pairing sheet has always used.
 *
 * A VEHICLE TAKES A PAIR, AND THE PAIR CANNOT BE THE SAME BOX. The primary and the secondary rows
 * pick from one pool, so each has to exclude what the other holds — which is the rule
 * `availableTrackerRows` encodes, along with the exemption that keeps a row able to show the
 * tracker it already has (an assigned tracker is not in the available pool by definition).
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

/** The columns every tracker surface reads: what is written on the box, and the SIM inside it. */
export interface PickableTracker {
    id: number;
    serial_number: string | null;
    sim_number: string | null;
}

/**
 * The serial, or `#95` — NEVER BLANK.
 *
 * A paired tracker whose `gps_trackers` row has not arrived still has to read as paired, because
 * the gate it clears is already green and an empty row would say the opposite.
 */
export function trackerLabel(tracker: PickableTracker): string {
    const serial = tracker.serial_number == null ? '' : String(tracker.serial_number).trim();
    return serial || `#${tracker.id}`;
}

/** `SIM 0244…`, or the absence of one said out loud. */
export function trackerMeta(tracker: PickableTracker): string {
    const sim = tracker.sim_number?.trim();
    return sim ? `SIM ${sim}` : 'no SIM recorded';
}

/** Serial and SIM — either of the two numbers printed on the thing. */
export function trackerSearchText(tracker: PickableTracker): string {
    return [trackerLabel(tracker), tracker.sim_number ?? ''].filter(Boolean).join(' ');
}

/**
 * The trackers this row may pick from.
 *
 * THREE RULES, IN ORDER: whatever this row already holds stays (it is not in the free pool), the
 * other row's tracker goes (one box cannot be in two slots), and everything unassigned remains.
 */
export function availableTrackerRows(
    pool: PickableTracker[] | undefined,
    current: PickableTracker | null | undefined,
    excludeId: number | null | undefined,
): PickableTracker[] {
    const rows = (pool ?? []).filter((row) => row.id !== excludeId);
    if (current && current.id !== excludeId && !rows.some((row) => row.id === current.id)) {
        rows.unshift(current);
    }
    return rows;
}

/**
 * The refusal for a tracker that cannot be created, or null.
 *
 * DIGITS ONLY, BECAUSE THE COLUMN IS NUMERIC. `gps_trackers.serial_number` is a numeric column,
 * so a serial with a letter in it is refused by Postgres with a sentence about input syntax — a
 * true statement about the wrong subject. Saying it here says it about the serial.
 */
export function validateNewTracker(draft: { serial: string }): string | null {
    const serial = draft.serial.trim();
    if (serial === '') return 'Enter the serial number printed on the tracker.';
    if (!/^\d+$/.test(serial)) {
        return 'The serial number is stored as a number — enter the digits only.';
    }
    return null;
}
